import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAdminDocusignForms } from '../../agents/docusignFormsAdminAgent';
import './DocuSignFormsManager.css';

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'], ['DC', 'District of Columbia'],
];

const TEMPLATE_TYPES = [
  'purchase_agreement',
  'counter_offer',
  'addendum',
  'seller_disclosure',
  'lead_paint_disclosure',
  'well_septic_addendum',
  'radon_disclosure',
  'mud_disclosure',
  'financing_addendum',
];

// Template category (DocumentTemplate.property_type): a paperwork category,
// or none for the state default (docs/architecture/paperwork-categories.md
// §4.4, §4.6). The server validates it against its categories (400). WB
// numbers are internal/admin-only labels (legal copy §1.5).
export const TEMPLATE_CATEGORY_OPTIONS = [
  { value: '', label: 'None (state default)' },
  { value: 'residential', label: 'Residential (WB-11 style)' },
  { value: 'condo', label: 'Condominium (WB-14 style)' },
  { value: 'vacant_land', label: 'Vacant land (WB-13 style)' },
];

/** The category's admin label, or the raw legacy value. */
export const templateCategoryLabel = (value) =>
  TEMPLATE_CATEGORY_OPTIONS.find((o) => o.value === (value || ''))?.label || value;

const DEFAULT_FORM = {
  name: '',
  template_type: 'purchase_agreement',
  state_code: 'TX',
  property_type: '',
  html_content: '<html><body><h1>DocuSign Form</h1></body></html>',
  effective_date: new Date().toISOString().slice(0, 10),
  activate: true,
};

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

export default function DocuSignFormsManager() {
  const {
    forms,
    loading,
    error,
    isAdmin,
    createFormVersion,
    setFormActive,
    updateForm,
  } = useAdminDocusignForms();

  const [draft, setDraft] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== draft.html_content) {
      editorRef.current.innerHTML = draft.html_content || '';
    }
  }, [draft.html_content]);

  const grouped = useMemo(() => {
    const list = Array.isArray(forms) ? forms : [];
    const filtered = stateFilter
      ? list.filter((f) => (f.state_code || '') === stateFilter)
      : list;

    return filtered.reduce((acc, form) => {
      const state = form.state_code || 'GEN';
      const propertyType = form.property_type || '';
      const key = `${state}::${form.template_type}::${propertyType}`;
      if (!acc[key]) {
        acc[key] = { state, templateType: form.template_type, propertyType, items: [] };
      }
      acc[key].items.push(form);
      return acc;
    }, {});
  }, [forms, stateFilter]);

  const groups = useMemo(
    () => Object.values(grouped).sort((a, b) => `${a.state}-${a.templateType}`.localeCompare(`${b.state}-${b.templateType}`)),
    [grouped],
  );

  const onChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const applyEditorCommand = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    onChange('html_content', editorRef.current.innerHTML);
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    onChange('html_content', editorRef.current.innerHTML);
  };

  const resetDraft = () => {
    setEditingId(null);
    setCloneSourceId(null);
    setDraft(DEFAULT_FORM);
  };

  const loadIntoEditor = (item, { mode }) => {
    if (mode === 'edit') {
      setEditingId(item.id);
      setCloneSourceId(null);
    } else {
      setCloneSourceId(item.id);
      setEditingId(null);
    }
    const cloneSuffix = mode === 'clone'
      ? ` (copy ${new Date().toISOString().slice(0, 10)} ${new Date().toTimeString().slice(0, 5)})`
      : '';
    setDraft({
      name: mode === 'clone' ? `${item.name}${cloneSuffix}` : item.name,
      template_type: item.template_type || 'purchase_agreement',
      state_code: (item.state_code || 'TX').toUpperCase(),
      property_type: item.property_type || '',
      html_content: item.html_content || '<html><body><h1>DocuSign Form</h1></body></html>',
      effective_date: mode === 'clone'
        ? new Date().toISOString().slice(0, 10)
        : (item.effective_date || new Date().toISOString().slice(0, 10)),
      activate: mode === 'clone' ? false : !!item.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const liveHtml = editorRef.current ? editorRef.current.innerHTML : draft.html_content;
      if (editingId) {
        const payload = {
          name: draft.name,
          html_content: liveHtml,
        };
        if (draft.effective_date) payload.effective_date = draft.effective_date;
        if (draft.property_type !== undefined) {
          payload.property_type = draft.property_type
            ? draft.property_type.trim().toLowerCase()
            : null;
        }
        await updateForm(editingId, payload);
        setActionSuccess(`Version #${editingId} saved.`);
      } else {
        await createFormVersion({
          ...draft,
          html_content: liveHtml,
          state_code: draft.state_code.trim().toUpperCase(),
          property_type: draft.property_type ? draft.property_type.trim().toLowerCase() : null,
          activate: !!draft.activate,
        });
        setActionSuccess(
          draft.activate
            ? 'New version created and set active.'
            : 'New version created (inactive). Use "Set active" to publish it.',
        );
      }
      resetDraft();
    } catch (err) {
      setActionError(err?.message || 'Failed to save DocuSign form');
    } finally {
      setBusy(false);
    }
  };

  const handleSetActive = async (templateId, value) => {
    setBusy(true);
    setActionError(null);
    try {
      await setFormActive(templateId, value);
    } catch (err) {
      setActionError(err?.message || 'Failed to update form status');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="admin-shell"><div className="admin-loading">Loading DocuSign forms...</div></div>;
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <div className="admin-forbidden">
          <h2 className="admin-forbidden-title">Access Denied</h2>
          <p>
            You do not have permission to manage DocuSign forms. If you are an admin,
            confirm your email is on the allowlist and refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <div className="admin-header">
        <h1 className="admin-title">DocuSign State Forms</h1>
        <p className="admin-subtitle">
          Manage state-specific forms with version history and effective dates.
        </p>
      </div>

      {(error || actionError) && (
        <div className="admin-banner admin-banner--error">{actionError || error}</div>
      )}
      {actionSuccess && (
        <div className="admin-banner admin-banner--info">{actionSuccess}</div>
      )}

      <section className="admin-section">
        <h2 className="admin-section-title">
          {editingId ? `Edit Version #${editingId}` : 'Create New Version'}
        </h2>
        <p className="admin-section-desc">
          {editingId
            ? 'Saving updates this version in place. To create a new version, cancel and use Clone instead.'
            : 'Publishing here creates the next version for the chosen state + template type.'}
        </p>
        {cloneSourceId && !editingId && (
          <div className="admin-banner admin-banner--info">
            Cloning from version ID #{cloneSourceId}. Edit content and publish as a new version.
          </div>
        )}
        <form className="docusign-form-grid" onSubmit={handleSubmit}>
          <label className="docusign-field">
            <span className="docusign-field-label">Form name</span>
            <input
              className="admin-input"
              placeholder="e.g. Wisconsin Residential Offer to Purchase v2"
              value={draft.name}
              onChange={(e) => onChange('name', e.target.value)}
              required
            />
            <span className="docusign-field-hint">
              Names must be unique across all versions. Edit this freely — the server will auto-suffix if a collision sneaks through.
            </span>
          </label>
          <select
            className="admin-input"
            value={draft.template_type}
            onChange={(e) => onChange('template_type', e.target.value)}
            disabled={!!editingId}
            required
          >
            {TEMPLATE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            className="admin-input"
            value={draft.state_code}
            onChange={(e) => onChange('state_code', e.target.value)}
            disabled={!!editingId}
            required
          >
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>{code} — {name}</option>
            ))}
          </select>
          <label className="docusign-field">
            <span className="docusign-field-label">Template category</span>
            <select
              className="admin-input"
              value={draft.property_type || ''}
              onChange={(e) => onChange('property_type', e.target.value)}
              disabled={!!editingId}
              data-testid="template-category-select"
            >
              {TEMPLATE_CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'none'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {/* A row saved before categories keeps its value visible. */}
              {draft.property_type &&
                !TEMPLATE_CATEGORY_OPTIONS.some((opt) => opt.value === draft.property_type) && (
                  <option value={draft.property_type}>Legacy: {draft.property_type}</option>
                )}
            </select>
            <span className="docusign-field-hint">
              Condominium and vacant land never fall back to the state default: without an active template in that category, those listings can't generate an offer.
            </span>
          </label>
          <input
            className="admin-input"
            type="date"
            value={draft.effective_date}
            onChange={(e) => onChange('effective_date', e.target.value)}
            required
          />
          {!editingId && (
            <label className="docusign-activate-toggle">
              <input
                type="checkbox"
                checked={!!draft.activate}
                onChange={(e) => onChange('activate', e.target.checked)}
              />
              <span>
                Make this version active on save
                {' '}
                <em className="docusign-activate-hint">
                  (any other active version in this state + template + property type will be deactivated)
                </em>
              </span>
            </label>
          )}
          <div className="docusign-editor-shell">
            <div className="docusign-editor-toolbar">
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('bold')}><strong>B</strong></button>
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('italic')}><em>I</em></button>
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('underline')}><u>U</u></button>
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('formatBlock', '<h2>')}>H2</button>
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('formatBlock', '<p>')}>P</button>
              <button type="button" className="admin-btn" onClick={() => applyEditorCommand('insertUnorderedList')}>• List</button>
            </div>
            <div className="docusign-editor-grid">
              <div>
                <label className="docusign-editor-label">Rich editor</label>
                <div
                  ref={editorRef}
                  className="admin-input docusign-form-html-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleEditorInput}
                />
              </div>
              <div>
                <label className="docusign-editor-label">Live preview</label>
                <div
                  className="docusign-form-html-preview"
                  dangerouslySetInnerHTML={{ __html: draft.html_content || '' }}
                />
              </div>
            </div>
          </div>
          <div className="docusign-form-actions">
            <button type="submit" className="admin-btn admin-btn--add" disabled={busy}>
              {editingId ? 'Save changes' : 'Create version'}
            </button>
            {(editingId || cloneSourceId) && (
              <button type="button" className="admin-btn" onClick={resetDraft} disabled={busy}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="admin-section">
        <div className="docusign-list-header">
          <h2 className="admin-section-title">Existing Versions</h2>
          <select
            className="admin-input docusign-state-filter"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
          >
            <option value="">All states</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>{code} — {name}</option>
            ))}
          </select>
        </div>
        {groups.length === 0 ? (
          <p className="admin-empty">No forms match this filter.</p>
        ) : (
          <div className="docusign-groups">
            {groups.map((group) => {
              const activeItem = group.items.find((i) => i.is_active);
              return (
              <article
                key={`${group.state}-${group.templateType}-${group.propertyType}`}
                className="docusign-group-card"
              >
                <header className="docusign-group-header">
                  <h3>
                    {group.state} · {group.templateType}
                    {group.propertyType && (
                      <span className="docusign-group-subtype"> · {templateCategoryLabel(group.propertyType)}</span>
                    )}
                  </h3>
                  {activeItem ? (
                    <span className="docusign-active-summary">
                      <span className="docusign-badge docusign-badge--active">ACTIVE</span>
                      v{activeItem.version} · effective {formatDate(activeItem.effective_date)}
                    </span>
                  ) : (
                    <span className="docusign-active-summary docusign-active-summary--none">
                      No active version — generation will fall back to a less specific template.
                    </span>
                  )}
                </header>
                <div className="docusign-versions">
                  {group.items.map((item) => (
                    <div key={item.id} className={`docusign-version-row ${item.is_active ? 'is-active' : 'is-inactive'}`}>
                      <div className="docusign-version-main">
                        <div className="docusign-version-title">
                          {item.is_active && (
                            <span className="docusign-badge docusign-badge--active">ACTIVE</span>
                          )}
                          <strong>v{item.version}</strong>
                          <span className="docusign-version-name">{item.name}</span>
                        </div>
                        <div className="docusign-version-meta">
                          Effective: {formatDate(item.effective_date)} · Updated: {formatDate(item.updated_at)}
                        </div>
                      </div>
                      <div className="docusign-version-actions">
                        <button
                          className="admin-btn"
                          type="button"
                          disabled={busy}
                          onClick={() => loadIntoEditor(item, { mode: 'edit' })}
                        >
                          Edit
                        </button>
                        <button
                          className={`admin-btn ${item.is_active ? 'admin-btn--remove' : 'admin-btn--add'}`}
                          type="button"
                          disabled={busy}
                          onClick={() => handleSetActive(item.id, !item.is_active)}
                        >
                          {item.is_active ? 'Deactivate' : 'Set active'}
                        </button>
                        <button
                          className="admin-btn"
                          type="button"
                          disabled={busy}
                          onClick={() => loadIntoEditor(item, { mode: 'clone' })}
                        >
                          Clone as new version
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
